import { Flex, Stack, useMantineTheme } from '@mantine/core';
import debounce from 'debounce-promise';
import { when } from 'mobx';
import type { JSX } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ISelectOption } from '@feathr/components';
import {
  AlertV2 as Alert,
  EAlertV2Type,
  NumberEntry,
  Skeleton,
  Text,
  UniversalSelect,
  Well,
} from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';

import type { IMediaBalanceFormErrors, IProjectMediaBalance } from '../RedeemCreditsModal.utils';
import { formatCredits, formatCurrency } from '../RedeemCreditsModal.utils';

import * as styles from '../RedemptionTypeSelect/RedemptionTypeSelect.css';

export interface IMediaBalanceFormProps {
  availableCredits: number;
  convertedAmount: number;
  creditsToRedeem: number | undefined;
  isCalculating?: boolean;
  isLoadingProject?: boolean;
  onCreditsChange: (credits: number | undefined) => void;
  onProjectChange: (projectId: string) => void;
  projectBalance?: IProjectMediaBalance;
  projectName: string | undefined;
  selectedProjectId: string | undefined;
  validationErrors: IMediaBalanceFormErrors;
}

/**
 * Step 2-3: Form for selecting a project and entering credits amount.
 * Displays conversion preview and project media balance information.
 */
function MediaBalanceForm({
  availableCredits,
  convertedAmount,
  creditsToRedeem,
  isCalculating,
  isLoadingProject,
  onCreditsChange,
  onProjectChange,
  projectBalance,
  projectName,
  selectedProjectId,
  validationErrors,
}: Readonly<IMediaBalanceFormProps>): JSX.Element {
  const { t } = useTranslation();
  const { Events: Projects } = useStore();
  const {
    spacing: { '2': innerGap, '4': sectionGap },
  } = useMantineTheme();
  const [options, setOptions] = useState<ISelectOption[]>();

  const loadProjects = useCallback(
    async (inputValue?: string): Promise<ISelectOption[]> => {
      const projects = Projects.list({
        filters: inputValue ? { name__icontains: inputValue } : undefined,
        only: ['id', 'name'],
        ordering: ['name'],
        pagination: { page: 0, page_size: 20 },
      });
      await when(() => projects.isLoaded);
      const projectOptions = projects.models.map(({ id, name }) => ({ id, name }));
      setOptions(projectOptions);
      return projectOptions;
    },
    [Projects],
  );

  const debouncedLoadProjects = useMemo(
    () => debounce(loadProjects, DEFAULT_DEBOUNCE_WAIT),
    [loadProjects],
  );

  function handleProjectSelect({ id }: ISelectOption): void {
    onProjectChange(id);
  }

  function handleCreditsChange(value: string | number): void {
    if (typeof value === 'number') {
      onCreditsChange(value);
      return;
    }
    const numValue = value === '' ? undefined : Number(value);
    onCreditsChange(numValue);
  }

  const isFormComplete =
    selectedProjectId &&
    creditsToRedeem !== undefined &&
    creditsToRedeem > 0 &&
    !validationErrors.credits &&
    !validationErrors.project;

  return (
    <Stack gap={sectionGap}>
      <Well className={styles.availableCredits} theme={'blue'}>
        <Text.DefaultHeadingMedium className={styles.availableCreditsText}>
          {t('Available Credits')}
        </Text.DefaultHeadingMedium>
        <Text.DefaultHeadingMedium className={styles.availableCreditsText}>
          {availableCredits.toLocaleString()}
        </Text.DefaultHeadingMedium>
      </Well>

      <UniversalSelect<ISelectOption>
        async={true}
        cacheOptions={true}
        defaultOptions={true}
        getOptionLabel={({ name }: ISelectOption): string => name}
        getOptionValue={({ id }: ISelectOption): string => id}
        label={t('Project')}
        loadOptions={debouncedLoadProjects}
        name={'project-select'}
        onSelectSingle={handleProjectSelect}
        placeholder={t('Select a project')}
        value={options?.find(({ id }) => id === selectedProjectId)}
      />

      {selectedProjectId && (isLoadingProject || projectBalance) && (
        <Well theme={'neutral'}>
          <Stack gap={innerGap} w={'100%'}>
            <Flex align={'center'} justify={'space-between'} w={'100%'}>
              <Text.DefaultHeadingMedium>{t('Current Media Balance')}</Text.DefaultHeadingMedium>
              <Text.DefaultRegular>
                {isLoadingProject ? (
                  <Skeleton width={60} />
                ) : (
                  formatCurrency(projectBalance?.currentBalance ?? 0)
                )}
              </Text.DefaultRegular>
            </Flex>
            <Flex align={'center'} justify={'space-between'} w={'100%'}>
              <Text.DefaultHeadingMedium>
                {t('Spent in {{monthYear}}', {
                  monthYear: new Date().toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  }),
                })}
              </Text.DefaultHeadingMedium>
              <Text.DefaultRegular>
                {isLoadingProject ? (
                  <Skeleton width={60} />
                ) : (
                  formatCurrency(projectBalance?.spent ?? 0)
                )}
              </Text.DefaultRegular>
            </Flex>
          </Stack>
        </Well>
      )}

      <NumberEntry
        description={t('Enter the number of credits you want to convert to media balance.')}
        error={creditsToRedeem !== undefined ? validationErrors.credits : undefined}
        label={t('Credits to Redeem')}
        max={availableCredits}
        min={1}
        name={'credits-to-redeem'}
        onChange={handleCreditsChange}
        value={creditsToRedeem ?? 0}
      />

      {isFormComplete && (
        <Alert
          description={
            isCalculating ? (
              t('Calculating conversion...')
            ) : (
              <>
                {t('Redeeming')} <strong>{formatCredits(creditsToRedeem)}</strong> {t('credits')} ={' '}
                <strong>{formatCurrency(convertedAmount)}</strong> {t('added to')}{' '}
                <strong>{projectName ?? t('selected project')}</strong> {t('media balance')}
              </>
            )
          }
          title={t('Conversion summary')}
          type={EAlertV2Type.info}
        />
      )}
    </Stack>
  );
}

export default MediaBalanceForm;
